<template>
	<section class="provider-orders-page">
		<div class="page-heading">
			<div>
				<h2>订单管理</h2>
				<p>查看当前店铺商品的购买订单，审核通过后由商家发货并发放商品权益</p>
			</div>
			<div class="heading-total">
				<span>订单总数</span>
				<strong>{{ pageOptions.total }}</strong>
			</div>
		</div>

		<div class="order-filter-card">
			<el-form :model="queryParams" class="order-search-form" label-position="right" label-width="72px">
				<el-form-item label="订单编号">
					<el-input v-model.trim="queryParams.orderCode" clearable placeholder="请输入订单编号"></el-input>
				</el-form-item>
				<el-form-item label="商品名称">
					<el-input v-model.trim="queryParams.goodsTitle" clearable placeholder="请输入商品名称"></el-input>
				</el-form-item>
				<el-form-item label="购买人">
					<el-input v-model.trim="queryParams.realName" clearable placeholder="请输入购买人姓名"></el-input>
				</el-form-item>
				<el-form-item label="审核状态">
					<el-select v-model="queryParams.auditState" clearable placeholder="全部状态">
						<el-option label="待财务审核" value="10"></el-option>
						<el-option label="待管理员审核" value="0"></el-option>
						<el-option label="审核通过" value="1"></el-option>
						<el-option label="审核不通过" value="2"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item label="发货状态">
					<el-select v-model="queryParams.shippingStatus" clearable placeholder="全部状态">
						<el-option label="未发货" :value="0"></el-option>
						<el-option label="已发货" :value="1"></el-option>
						<el-option label="已收货" :value="2"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item label="下单时间">
					<el-date-picker
						v-model="orderTimeRange"
						type="daterange"
						start-placeholder="开始日期"
						end-placeholder="结束日期"
						value-format="yyyy-MM-dd"
						range-separator="至"
						clearable
					></el-date-picker>
				</el-form-item>
				<div class="search-actions">
					<el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
					<el-button icon="el-icon-refresh-left" @click="handleReset">重置</el-button>
				</div>
			</el-form>
		</div>

		<div class="order-table-card">
			<div class="table-heading">
				<div>
					<strong>商品订单</strong>
					<span>仅展示当前店铺商品被购买后生成的订单</span>
				</div>
				<el-button icon="el-icon-refresh" size="small" @click="loadData">刷新</el-button>
			</div>

			<el-table v-loading="loading" :data="tableData" border class="order-table" empty-text="暂无商品订单">
				<el-table-column label="订单信息" min-width="190">
					<template slot-scope="{ row }">
						<div class="order-meta">
							<strong>{{ row.orderCode || '-' }}</strong>
							<span>下单：{{ row.createTime || '-' }}</span>
							<span v-if="row.payTime">支付：{{ row.payTime }}</span>
							<span>订单状态：{{ orderStateText(row.orderState) }}</span>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="商品信息" min-width="310">
					<template slot-scope="{ row }">
						<div class="goods-cell" @click="handleGoodsDetail(row)">
							<el-image class="goods-cover" :src="getGoodsImage(row)" fit="cover">
								<div slot="error" class="cover-placeholder"><i class="el-icon-picture-outline"></i></div>
							</el-image>
							<div class="goods-copy">
								<strong :title="row.goodsTitle">{{ row.goodsTitle || '-' }}</strong>
								<span>
									{{ goodsTypeText(row.goodsType) }}
									<template v-if="row.measureUnit">· {{ row.measureUnit }}</template>
								</span>
								<small>商品编号：{{ row.sourceGoodsId || '-' }}</small>
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="购买人" min-width="120" align="center">
					<template slot-scope="{ row }">{{ row.realName || '-' }}</template>
				</el-table-column>
				<el-table-column label="数量" width="86" align="center">
					<template slot-scope="{ row }">{{ row.purchaseQuantity || 1 }}</template>
				</el-table-column>
				<el-table-column label="实付金额" width="125" align="right">
					<template slot-scope="{ row }">
						<strong class="amount-text">¥ {{ amountText(row.actualPayment) }}</strong>
					</template>
				</el-table-column>
				<el-table-column label="支付方式" width="118" align="center">
					<template slot-scope="{ row }">{{ paymentTypeText(row.paymentType) }}</template>
				</el-table-column>
				<el-table-column label="审核状态" width="126" align="center">
					<template slot-scope="{ row }">
						<span :class="['status-pill', auditStatus(row.auditState).className]">{{ auditStatus(row.auditState).text }}</span>
					</template>
				</el-table-column>
				<el-table-column label="发货状态" width="110" align="center">
					<template slot-scope="{ row }">
						<span :class="['status-pill', shippingStatus(row.shippingStatus).className]">
							{{ shippingStatus(row.shippingStatus).text }}
						</span>
					</template>
				</el-table-column>
				<el-table-column label="操作" width="116" align="center" fixed="right">
					<template slot-scope="{ row }">
						<el-tooltip :content="shippingTip(row)" placement="top">
							<span>
								<el-button
									size="mini"
									type="primary"
									:plain="!canPrepareShipment(row)"
									:disabled="isShipmentFinished(row)"
									:loading="shippingOrderId === row.id"
									@click="handleShipment(row)"
								>
									{{ shipmentButtonText(row) }}
								</el-button>
							</span>
						</el-tooltip>
					</template>
				</el-table-column>
			</el-table>

			<div class="order-pagination">
				<el-pagination
					background
					layout="total, sizes, prev, pager, next, jumper"
					:total="pageOptions.total"
					:current-page.sync="pageOptions.page"
					:page-size.sync="pageOptions.limit"
					:page-sizes="[10, 20, 50, 100]"
					@size-change="handleSizeChange"
					@current-change="handleCurrentChange"
				></el-pagination>
			</div>
		</div>
	</section>
</template>

<script>
	import { previewMerchantFile, providerShipMerchantOrder, queryMyShopGoodsOrderPage } from '@/api/mall'

	const createQueryParams = () => ({
		orderCode: '',
		goodsTitle: '',
		realName: '',
		auditState: '',
		shippingStatus: ''
	})

	export default {
		name: 'ProviderOrders',
		data() {
			return {
				loading: false,
				shippingOrderId: '',
				tableData: [],
				queryParams: createQueryParams(),
				orderTimeRange: [],
				pageOptions: {
					page: 1,
					limit: 20,
					total: 0
				}
			}
		},
		created() {
			this.loadData()
		},
		methods: {
			async loadData() {
				this.loading = true
				try {
					const response = await queryMyShopGoodsOrderPage({
						page: this.pageOptions.page,
						size: this.pageOptions.limit,
						...this.queryParams,
						startTime: this.orderTimeRange && this.orderTimeRange[0] ? this.orderTimeRange[0] : '',
						endTime: this.orderTimeRange && this.orderTimeRange[1] ? this.orderTimeRange[1] : ''
					})
					const data = response.data || {}
					this.tableData = data.records || data.list || []
					this.pageOptions.total = Number(data.total || 0)
				} finally {
					this.loading = false
				}
			},
			handleSearch() {
				this.pageOptions.page = 1
				this.loadData()
			},
			handleReset() {
				this.queryParams = createQueryParams()
				this.orderTimeRange = []
				this.pageOptions.page = 1
				this.pageOptions.limit = 20
				this.loadData()
			},
			handleSizeChange(size) {
				this.pageOptions.page = 1
				this.pageOptions.limit = size
				this.loadData()
			},
			handleCurrentChange(page) {
				this.pageOptions.page = page
				this.loadData()
			},
			handleGoodsDetail(row) {
				if (!row.sourceGoodsId) return
				const goodsPath = Number(row.goodsType) === 2 ? 'exhibition' : 'self'
				this.$router.push(`/mallStore/goods/${goodsPath}/${row.sourceGoodsId}`)
			},
			async handleShipment(row) {
				if (this.shippingOrderId) return
				if (this.isShipmentFinished(row)) {
					this.$message.warning(this.shippingStatus(row.shippingStatus).text === '已收货' ? '该订单已确认收货' : '该订单已经发货')
					return
				}
				// 审核未通过时给出明确反馈，避免禁用按钮后点击没有任何响应。
				if (!this.isAuditPassed(row)) {
					this.$message.warning('订单尚未完成管理端审核，暂不能发货')
					return
				}
				try {
					await this.$modal.confirm(`确认对订单 ${row.orderCode || ''} 发货吗？发货成功后将同步发放该商品绑定的权益。`)
					this.shippingOrderId = row.id
					await providerShipMerchantOrder({ id: row.id })
					this.$message.success('发货成功，商品权益已发放给购买用户')
					await this.loadData()
				} catch (error) {
					if (error !== 'cancel' && error !== 'close') {
						console.error('服务商发货失败', error)
					}
				} finally {
					this.shippingOrderId = ''
				}
			},
			getGoodsImage(row) {
				const image = row.mainImage || ''
				if (!image) return ''
				if (/^https?:\/\//.test(image) || image.indexOf('data:image') === 0) return image
				return previewMerchantFile() + image
			},
			goodsTypeText(value) {
				return Number(value) === 2 ? '展销商品' : '自营商品'
			},
			amountText(value) {
				const amount = Number(value)
				return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
			},
			paymentTypeText(value) {
				return (
					{
						WECHAT_PAY: '微信支付',
						ALIPAY: '支付宝',
						BANK_AGGREGATE: '聚合码支付'
					}[value] ||
					value ||
					'-'
				)
			},
			orderStateText(value) {
				return (
					{
						0: '待支付',
						1: '已支付',
						2: '已取消',
						4: '已作废',
						10: '待审核'
					}[Number(value)] || '-'
				)
			},
			auditStatus(value) {
				return (
					{
						0: { text: '待管理员审核', className: 'is-warning' },
						1: { text: '审核通过', className: 'is-success' },
						2: { text: '审核不通过', className: 'is-danger' },
						10: { text: '待财务审核', className: 'is-finance' }
					}[Number(value)] || { text: '-', className: 'is-default' }
				)
			},
			shippingStatus(value) {
				const status = this.shippingStatusValue(value)
				return (
					{
						0: { text: '未发货', className: 'is-warning' },
						1: { text: '已发货', className: 'is-success' },
						2: { text: '已收货', className: 'is-finished' }
					}[status] || { text: '未发货', className: 'is-warning' }
				)
			},
			shippingStatusValue(value) {
				const text = String(value === undefined || value === null ? '' : value).trim()
				if (text === '2' || text === '已收货') return 2
				if (text === '1' || text === '已发货') return 1
				return 0
			},
			isAuditPassed(row) {
				const auditState = String(row && row.auditState !== undefined ? row.auditState : '').trim()
				const orderState = String(row && row.orderState !== undefined ? row.orderState : '').trim()
				return (auditState === '1' || auditState === '审核通过') && (orderState === '1' || orderState === '已支付')
			},
			canPrepareShipment(row) {
				return this.isAuditPassed(row) && this.shippingStatusValue(row.shippingStatus) === 0
			},
			isShipmentFinished(row) {
				return this.shippingStatusValue(row && row.shippingStatus) > 0
			},
			shipmentButtonText(row) {
				if (this.shippingStatusValue(row.shippingStatus) === 2) return '已收货'
				if (this.shippingStatusValue(row.shippingStatus) === 1) return '已发货'
				return '发货'
			},
			shippingTip(row) {
				if (this.shippingStatusValue(row.shippingStatus) === 2) return '买家已确认收货'
				if (this.shippingStatusValue(row.shippingStatus) === 1) return '该订单已发货'
				if (!this.isAuditPassed(row)) return '管理端审核通过后才可发货'
				return '发货后将按订单快照向购买用户发放商品权益'
			}
		}
	}
</script>

<style lang="scss" scoped>
	.provider-orders-page {
		min-height: 100%;
		padding: 24px;
		border: 1px solid #e4e9ef;
		border-radius: 8px;
		background: #ffffff;
		box-sizing: border-box;
	}

	.page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-bottom: 19px;
		border-bottom: 1px solid #e9edf2;

		h2 {
			margin: 0 0 6px;
			color: #303846;
			font-size: 21px;
		}
		p {
			margin: 0;
			color: #929aa6;
			font-size: 13px;
		}
	}

	.heading-total {
		display: flex;
		align-items: baseline;
		gap: 8px;
		padding: 9px 14px;
		border: 1px solid #dbe9f8;
		border-radius: 6px;
		color: #6c7887;
		background: #f5f9fe;

		strong {
			color: #2671cb;
			font-size: 22px;
		}
	}

	.order-filter-card {
		margin-top: 14px;
		padding: 12px 14px;
		border: 1px solid #e4ebf3;
		border-radius: 7px;
		background: #f8fbfe;
	}

	.order-search-form {
		display: grid;
		grid-template-columns:
			minmax(195px, 1fr)
			minmax(195px, 1fr)
			minmax(170px, 0.85fr)
			minmax(170px, 0.85fr)
			minmax(160px, 0.8fr)
			minmax(285px, 1.35fr)
			auto;
		align-items: center;
		gap: 10px;

		::v-deep .el-form-item {
			margin-bottom: 0;
		}
		::v-deep .el-form-item__label {
			padding: 0 8px 0 0;
			color: #526072;
			font-weight: 500;
			line-height: 36px;
		}
		::v-deep .el-form-item__content {
			line-height: 36px;
		}
		::v-deep .el-select,
		::v-deep .el-date-editor {
			width: 100%;
		}
		::v-deep .el-input__inner,
		::v-deep .el-range-editor.el-input__inner {
			height: 36px;
			line-height: 36px;
		}
		::v-deep .el-range-editor .el-range-separator,
		::v-deep .el-range-editor .el-range__icon,
		::v-deep .el-range-editor .el-range__close-icon {
			line-height: 28px;
		}
	}

	.search-actions {
		display: flex;
		align-items: center;
		white-space: nowrap;

		.el-button {
			height: 36px;
			padding: 0 15px;
		}
	}

	.order-table-card {
		margin-top: 16px;
		padding: 18px;
		border: 1px solid #e4e9ef;
		border-radius: 7px;
	}

	.table-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 15px;

		strong {
			color: #303b4c;
			font-size: 17px;
		}
		span {
			margin-left: 12px;
			color: #969faa;
			font-size: 13px;
		}
	}

	.order-table {
		width: 100%;

		::v-deep th {
			height: 46px;
			padding: 0;
			color: #344154;
			background: #f2f7fc;
		}
		::v-deep td {
			border-color: #e3eaf1;
		}
		::v-deep .el-table__body td {
			padding: 11px 0;
		}
		::v-deep .el-table__row:hover > td {
			background: #f8fbff;
		}
	}

	.order-meta {
		strong,
		span {
			display: block;
		}
		strong {
			color: #344154;
			font-size: 14px;
		}
		span {
			margin-top: 6px;
			color: #8a95a3;
			font-size: 12px;
		}
	}

	.goods-cell {
		display: flex;
		align-items: center;
		cursor: pointer;
	}

	.goods-cover {
		width: 78px;
		height: 58px;
		margin-right: 12px;
		flex-shrink: 0;
		border: 1px solid #dce5ee;
		border-radius: 5px;
		background: #f5f7fa;
	}

	.cover-placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		color: #bdc5cf;
		font-size: 24px;
	}

	.goods-copy {
		min-width: 0;

		strong,
		span,
		small {
			display: block;
		}
		strong {
			max-width: 220px;
			overflow: hidden;
			color: #2f3b4c;
			font-size: 14px;
			text-overflow: ellipsis;
			white-space: nowrap;
		}
		span {
			margin-top: 5px;
			color: #647184;
			font-size: 12px;
		}
		small {
			margin-top: 4px;
			color: #9aa3ae;
			font-size: 11px;
		}
	}

	.amount-text {
		color: #e45c32;
		font-size: 14px;
	}

	.status-pill {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 68px;
		height: 26px;
		padding: 0 9px;
		border-radius: 13px;
		font-size: 12px;
		font-weight: 600;
		box-sizing: border-box;

		&.is-success {
			color: #168653;
			background: #e5f6ed;
		}
		&.is-warning {
			color: #ae741d;
			background: #fff2da;
		}
		&.is-finance {
			color: #3970bb;
			background: #e9f2ff;
		}
		&.is-danger {
			color: #d34e4e;
			background: #feebeb;
		}
		&.is-finished,
		&.is-default {
			color: #748090;
			background: #edf0f3;
		}
	}

	.order-pagination {
		display: flex;
		justify-content: center;
		margin-top: 22px;
	}

	@media (max-width: 1700px) {
		.order-search-form {
			grid-template-columns: repeat(4, minmax(190px, 1fr));
		}
		.search-actions {
			justify-content: flex-end;
			grid-column: 4;
		}
	}

	@media (max-width: 1100px) {
		.order-search-form {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
		.search-actions {
			grid-column: 2;
		}
	}

	@media (max-width: 760px) {
		.provider-orders-page {
			padding: 16px;
		}
		.page-heading,
		.table-heading {
			align-items: flex-start;
			gap: 14px;
		}
		.order-search-form {
			grid-template-columns: 1fr;
		}
		.search-actions {
			grid-column: 1;
			justify-content: flex-start;
		}
	}
</style>
